import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Paperclip, Pencil, Plus, Trash2 } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { adminApi, formatApiError } from "@/lib/adminApi";
import { TodoDetail } from "@/components/admin/todos/TodoDetail";

export const STATUSES = ["Created", "In Progress", "Issue with Completion", "Done – Pending Review", "Complete"];
export const STATUS_KEY = { "Created": "created", "In Progress": "progress", "Issue with Completion": "issue", "Done – Pending Review": "review", "Complete": "complete" };
export const err = (e) => toast.error(formatApiError(e.response?.data?.detail));
export const fmtDate = (d) => (d ? new Date(d.length === 10 ? `${d}T12:00:00` : d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "");

export const StatusSelect = ({ value, onChange, testId }) => {
  const [busy, setBusy] = useState(false);
  const change = async (v) => {
    if (busy || v === value) return;
    setBusy(true);
    try { await onChange(v); } finally { setBusy(false); }
  };
  return (
  <select className={`td-status td-status--${STATUS_KEY[value]}`} value={value} disabled={busy} aria-busy={busy} onChange={(e) => change(e.target.value)} onClick={(e) => e.stopPropagation()} data-testid={testId}>
    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
  </select>
  );
};

const PersonRow = ({ p, active, onSelect, onRename, onDelete }) => (
  <li className={`td-person ${active ? "is-on" : ""}`}>
    <button className="td-person__main" onClick={onSelect} data-testid={`todo-person-${p.id}`}>
      <span className="td-avatar" style={{ background: p.color }}>{p.initial}</span>
      <span className="td-person__name">{p.name}</span>
      <span className="td-person__count" data-testid={`todo-person-count-${p.id}`}>{p.open_count}</span>
    </button>
    {active && (
      <span className="td-person__tools">
        <button className="td-icon" onClick={onRename} aria-label="Rename list" data-testid="todo-person-rename"><Pencil size={13} /></button>
        <button className="td-icon" onClick={onDelete} aria-label="Delete list" data-testid="todo-person-delete"><Trash2 size={13} /></button>
      </span>
    )}
  </li>
);

const TodoRow = ({ t, onOpen, onStatus }) => (
  <li className={`td-row ${t.overdue ? "is-overdue" : ""}`} onClick={onOpen} data-testid={`todo-row-${t.id}`}>
    <div className="td-row__main">
      <span className="td-row__title">{t.title}</span>
      <span className="td-row__meta">
        {t.due_date && <span className={t.overdue ? "td-overdue" : ""} data-testid={`todo-due-${t.id}`}>{t.overdue ? "Overdue · " : "Due "}{fmtDate(t.due_date)}</span>}
        <span className="td-row__created" data-testid={`todo-created-${t.id}`}>Created {new Date(t.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>
        {t.attachments.length > 0 && <span className="td-row__att"><Paperclip size={12} /> {t.attachments.length}</span>}
        {(t.client || t.project) && <span className="td-row__link">{t.project?.name || t.client?.full_name}</span>}
      </span>
    </div>
    <StatusSelect value={t.status} onChange={onStatus} testId={`todo-status-${t.id}`} />
  </li>
);

export const TodosTab = () => {
  const [people, setPeople] = useState(null);
  const [pid, setPid] = useState(null);
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const [showArchive, setShowArchive] = useState(false);
  const [title, setTitle] = useState("");
  const [due, setDue] = useState("");
  const [openId, setOpenId] = useState(null);
  const [delPerson, setDelPerson] = useState(false);
  const [naming, setNaming] = useState(null);

  const loadPeople = useCallback(() => adminApi.get("/todos/people").then((r) => { setPeople(r.data); setPid((cur) => cur && r.data.some((p) => p.id === cur) ? cur : r.data[0]?.id || null); }), []);
  const loadList = useCallback(() => (pid ? adminApi.get("/todos", { params: { person_id: pid } }).then((r) => setData(r.data)) : setData(null)), [pid]);
  useEffect(() => { loadPeople(); }, [loadPeople]);
  useEffect(() => { loadList(); }, [loadList]);
  const refresh = () => Promise.all([loadPeople(), loadList()]);

  const submitName = async (e) => {
    e.preventDefault();
    const name = naming.value.trim();
    if (!name) return;
    try {
      if (naming.mode === "new") { const { data: p } = await adminApi.post("/todos/people", { name }); await loadPeople(); setPid(p.id); toast.success(`List “${p.name}” created`); }
      else { await adminApi.patch(`/todos/people/${pid}`, { name }); loadPeople(); }
      setNaming(null);
    } catch (e2) { err(e2); }
  };
  const newPerson = () => setNaming({ mode: "new", value: "" });
  const rename = () => setNaming({ mode: "rename", value: people.find((p) => p.id === pid).name });
  const removePerson = async () => {
    try { await adminApi.delete(`/todos/people/${pid}`); setPid(null); toast.success("List deleted"); loadPeople(); } catch (e) { err(e); }
    setDelPerson(false);
  };
  const add = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    try { await adminApi.post("/todos", { person_id: pid, title, due_date: due || null }); setTitle(""); setDue(""); refresh(); } catch (e2) { err(e2); }
  };
  const setStatus = async (t, status) => {
    try {
      await adminApi.patch(`/todos/${t.id}`, { status });
      await refresh();
      if (status === "Complete") toast.success("Completed — moved to Archived");
      else if (t.status === "Complete") toast.success("Back in the active list");
    } catch (e) { err(e); }
  };

  if (!people) return <p className="hx-empty">Loading…</p>;
  const person = people.find((p) => p.id === pid);
  const active = data ? data.active.filter((t) => filter === "all" || t.status === filter) : [];
  const current = person && people.find((p) => p.id === pid);
  return (
    <div className="td" data-testid="todos-tab">
      <aside className="td-side">
        <div className="td-side__head"><span className="hx-label">Lists</span>
          <button className="hx-btn hx-btn--ghost hx-btn--sm" onClick={newPerson} data-testid="todo-new-person"><Plus size={13} /> New person</button></div>
        {naming && (
          <form className="td-nameform" onSubmit={submitName}>
            <input className="hx-input" autoFocus maxLength={60} placeholder={naming.mode === "new" ? "Person's name" : "New name"} value={naming.value} onChange={(e) => setNaming({ ...naming, value: e.target.value })} data-testid="todo-person-name-input" />
            <button className="hx-btn hx-btn--sm" disabled={!naming.value.trim()} data-testid="todo-person-name-save">{naming.mode === "new" ? "Create" : "Rename"}</button>
            <button type="button" className="hx-btn hx-btn--ghost hx-btn--sm" onClick={() => setNaming(null)} data-testid="todo-person-name-cancel">Cancel</button>
          </form>
        )}
        {people.length === 0 ? <p className="hx-muted">Create a list for each person.</p> : (
          <ul className="td-people">
            {people.map((p) => <PersonRow key={p.id} p={p} active={p.id === pid} onSelect={() => { setPid(p.id); setFilter("all"); }} onRename={rename} onDelete={() => setDelPerson(true)} />)}
          </ul>
        )}
      </aside>
      <section className="td-main">
        {!person ? <p className="hx-empty">Create a person's list to start adding to-dos.</p> : (
          <>
            <div className="td-main__head">
              <h2 className="hx-count" data-testid="todo-list-title">{person.name}</h2>
              <select className="hx-input td-filter" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="todo-filter">
                <option value="all">All active</option>
                {STATUSES.filter((s) => s !== "Complete").map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <form className="td-quick" onSubmit={add}>
              <input className="hx-input" placeholder="Add a to-do…" value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} data-testid="todo-quick-title" />
              <input className="hx-input td-quick__date" type="date" value={due} onChange={(e) => setDue(e.target.value)} aria-label="Due date (optional)" data-testid="todo-quick-due" />
              <button className="hx-btn hx-btn--sm" disabled={!title.trim()} data-testid="todo-quick-add">Add</button>
            </form>
            {!data ? <p className="hx-muted">Loading…</p> : active.length === 0 ? <p className="hx-empty" data-testid="todo-empty">{filter === "all" ? "Nothing open. Nice." : `No to-dos are ${filter}.`}</p> : (
              <ul className="td-list" data-testid="todo-active-list">
                {active.map((t) => <TodoRow key={t.id} t={t} onOpen={() => setOpenId(t.id)} onStatus={(s) => setStatus(t, s)} />)}
              </ul>
            )}
            {data && (
              <div className="td-archive" data-testid="todo-archive">
                <button className="td-archive__toggle" onClick={() => setShowArchive(!showArchive)} data-testid="todo-archive-toggle">
                  {showArchive ? <ChevronDown size={15} /> : <ChevronRight size={15} />} Archived ({data.archived.length})
                </button>
                {showArchive && (data.archived.length === 0 ? <p className="hx-muted" style={{ padding: "8px 0" }}>Completed to-dos land here.</p> : (
                  <ul className="td-list td-list--archived" data-testid="todo-archived-list">
                    {data.archived.map((t) => <TodoRow key={t.id} t={t} onOpen={() => setOpenId(t.id)} onStatus={(s) => setStatus(t, s)} />)}
                  </ul>
                ))}
              </div>
            )}
          </>
        )}
      </section>
      {openId && <TodoDetail id={openId} people={people} onClose={() => setOpenId(null)} onChanged={refresh} onMoved={(p) => { loadPeople(); setPid(p.id); }} />}
      <AlertDialog open={delPerson} onOpenChange={setDelPerson}>
        <AlertDialogContent data-testid="todo-person-delete-dialog">
          <AlertDialogTitle>Delete {current?.name}'s list?</AlertDialogTitle>
          <AlertDialogDescription>{current?.total_count ? `This list still has ${current.total_count} to-do(s), including archived ones. Move or delete them first.` : "The list is empty and will be removed."}</AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="todo-person-delete-cancel">Cancel</AlertDialogCancel>
            {!current?.total_count && <AlertDialogAction onClick={removePerson} data-testid="todo-person-delete-confirm">Delete list</AlertDialogAction>}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
